'use client';

import * as React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS, DARK_COLORS } from '@/constants';

import {
  BaseWrapper as BaseAside,
  InfoAside,
  SuccessAside,
  WarningAside,
} from '@/components/Aside';
import ColorModeContainer from '@/components/ColorModeContainer';

import DemoContext from './DemoContext';
import DefaultOutputWrapper, {
  Props as DefaultOutputWrapperProps,
} from './OutputWrapper';

type ChildrenRenderProp = (
  state: Record<string, any>,
  activeControlId: string | null
) => React.ReactNode;

type Controls = React.ReactNode | Array<React.ReactNode>;

type DivWithoutChildren = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children'
>;

export interface Props extends DivWithoutChildren {
  id?: string;
  initialValues: Record<string, any>;
  controls?: Controls;
  controlPosition?: 'top' | 'bottom';
  children: ChildrenRenderProp;

  // I often need to customize the layout/styles for the controls.
  // In simple cases, I can pass a `customGrid` set of CSS declarations, like { gridTemplateColumns: '1fr 1fr' }.
  customGrid?: React.CSSProperties;
  // In more complex cases, I can pass an entire component definition, typically a styled component. This allows me to use media queries to customize the grid layout based on viewport:
  CustomGridComponent?: React.ComponentType<any>;

  resizable?: boolean;
  outputWrapper?: React.ComponentType<any> | null;
  outputWrapperProps?: Partial<DefaultOutputWrapperProps>;
  // Sometimes, I need to reset something when one of the Demo controls is changed. The new state is not passed along, so this isn’t really meant to be used to show the main state somewhere else. For that, I can use `renderAfter`.
  handleStateChange?: () => void;

  wrapperStyle?: React.CSSProperties;

  // `renderAfter` allows us to put some stuff after the entire demo unit. Typically this is used when the next paragraph of a blog post depends on some of the demo unit’s state.
  renderAfter?: (state: Record<string, any>) => React.ReactNode;
  // Sometimes, I want to render a second row of controls on the opposite side. For example, if my primary controls are at the bottom, I might want to render a slider on top. This is essentially a slot that sits outside the "canvas" but still within the demo unit.
  renderAltControls?: (state: Record<string, any>) => React.ReactNode;
  ref?: React.MutableRefObject<HTMLDivElement | null>;
}

function Demo({
  id,
  initialValues,
  controls,
  controlPosition = 'bottom',
  customGrid,
  CustomGridComponent,
  resizable,
  outputWrapper: OutputWrapper = DefaultOutputWrapper,
  outputWrapperProps,
  handleStateChange,
  renderAfter,
  renderAltControls,
  wrapperStyle,
  children,
  ref,
  ...delegated
}: Props) {
  const defaultId = React.useId();
  const appliedId = id ?? defaultId;

  const [state, rawSetState] = React.useState(initialValues);

  const [activeControlId, setActiveControlId] = React.useState<
    string | null
  >(null);

  // Initial values are generally specified as an inline object, meaning it’ll get recreated on every render. I want to pass these initial values through context so that controls can implement a “handleReset” function, but I don’t want the whole demo to re-render when the initial values change. So I’ll pass a ref:
  const initialValuesRef = React.useRef(initialValues);
  initialValuesRef.current = initialValues;

  const setState = React.useCallback(
    (newState: React.SetStateAction<Record<string, any>>) => {
      rawSetState(newState);
      handleStateChange?.();
    },
    [handleStateChange]
  );

  const ControlsComponent = CustomGridComponent ?? DefaultControls;

  const includeControls = Array.isArray(controls)
    ? controls.length > 0
    : !!controls;

  const controlElem = includeControls && (
    <ControlsComponent style={customGrid}>
      {controls}
    </ControlsComponent>
  );

  const contextValue = React.useMemo(
    () => ({
      id: appliedId,
      state,
      setState,
      activeControlId,
      setActiveControlId,
      initialValuesRef,
    }),
    [appliedId, state, setState, activeControlId]
  );

  return (
    <DemoContext.Provider value={contextValue}>
      <OuterWrapper
        colorMode="dark"
        data-demo-unit="true"
        data-control-position={
          includeControls ? controlPosition : 'none'
        }
        style={wrapperStyle}
      >
        <Wrapper ref={ref} {...delegated}>
          {controls && controlPosition === 'top' && controlElem}
          {renderAltControls &&
            controlPosition === 'bottom' &&
            renderAltControls(state)}

          {OutputWrapper ? (
            <OutputWrapper
              resizable={resizable}
              {...outputWrapperProps}
            >
              {children(state, activeControlId)}
            </OutputWrapper>
          ) : (
            children(state, activeControlId)
          )}

          {controls && controlPosition === 'bottom' && controlElem}
          {renderAltControls &&
            controlPosition === 'top' &&
            renderAltControls(state)}
        </Wrapper>
      </OuterWrapper>
      {typeof renderAfter === 'function' && renderAfter(state)}
    </DemoContext.Provider>
  );
}

const OuterWrapper = styled(ColorModeContainer)`
  --color-background: ${DARK_COLORS['--syntax-bg']};
  margin-top: 8px;
  margin-inline: calc(var(--viewport-padding) * -1);
  margin-bottom: 48px;
  padding: var(--viewport-padding);
  background: var(--color-background);
  isolation: isolate;
  max-width: 100vw;
  border-radius: 6px;
  outline: 1px solid var(--color-content-outline);
  outline-offset: -1px;

  /*
    On smaller screens in Light Mode, we don't want any border radius. It looks a bit funny, since the dark demo rounds up against the darker screen bezel.
  */
  @media ${BREAKPOINTS.mdAndSmaller} {
    html[data-color-mode='light'] & {
      border-radius: 0px;
    }
  }

  &[data-control-position='top'] {
    /*
      On desktop, when the controls are on top, there's too much space. Shrink it from 32px to 24px for optical alignment.
      On mobile, when --viewport-padding shrinks to 16px, we actually benefit from MORE space.
    */
    padding-top: 24px;
  }

  ${BaseAside} && {
    outline: none;

    @media ${BREAKPOINTS.mdAndSmaller} {
      border-radius: 0;
      /*
        We don't want the demo to overflow. Normally, I'd set 'max-width: 100%', but that's complicated here by the negative-margin viewport-padding stuff. So we'll use vw.
      */
      max-width: 100vw;
    }
  }

  /* Mobile/Tablet styles for Demo inside <Aside> */
  @media ${BREAKPOINTS.mdAndSmaller} {
    ${BaseAside} && {
      margin-left: calc(
        var(--viewport-padding) * -1 + var(--aside-border-width)
      );
      margin-right: calc(var(--viewport-padding) * -1);
      padding: var(--viewport-padding);
      padding-left: calc(
        var(--viewport-padding) - var(--aside-border-width)
      );
    }
  }

  /* Desktop styles for Demo inside <Aside> */
  @media ${BREAKPOINTS.mdAndLarger} {
    ${BaseAside} && {
      margin-inline: calc(var(--viewport-padding) * -0.5);
      padding: calc(var(--viewport-padding) * 0.5);
    }
  }

  ${InfoAside} & {
    /* Replace the dark grey with a dark amber */
    --color-background: ${DARK_COLORS['--color-info-200']} !important;
  }
  ${WarningAside} & {
    --color-background: ${DARK_COLORS[
      '--color-warning-200'
    ]} !important;
  }
  ${SuccessAside} & {
    --color-background: ${DARK_COLORS[
      '--color-success-200'
    ]} !important;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 8px;
`;

const DefaultControls = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media ${BREAKPOINTS.mdAndLarger} {
    gap: 32px;
  }
`;

export default Demo;
